import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import {
  Option,
  Pagination,
  Select,
  Table as ShellTable,
} from '@sede-enterprise/shell-ds-react-framework';
import {
  THead,
  TBody,
  TH as Th,
  TD as Td,
  TR as Tr,
} from '@sede-enterprise/shell-ds-react-framework/build/esm/components/Table/components/TableElements/TableElements';
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
  flexRender,
} from '@tanstack/react-table';
import { v4 as uuidv4 } from 'uuid';
import { TableContainer, LoaderContainer } from './style';
import {
  ITable,
  IFilter,
  ISorting,
  ISortingOptions,
  ITableRef,
  IAdvFilter,
} from '../../types/UI/GenesisTable.type';
import ColumnTitle from './ColumnTitle';
import Loader from '../Loader';
import {
  sortingData,
  handleGenerateAdvFilter,
  handlePersistFilter,
  handlePersistSorting,
  PAGINATION_OPTIONS,
} from '../../utils/table';

const TABLE_FILTER = 'table-filter';

const LoaderComp = () => (
  <LoaderContainer>
    <div data-testid='loader' className='loader-wrapper'>
      <Loader />
    </div>
  </LoaderContainer>
);

const ColumnTitleComp = (column, sortedColumn, handleSort, handleFilter, filters) => {
  const { accessKey } = column;
  return (
    <ColumnTitle
      column={column}
      sortedColumn={sortedColumn}
      handleSort={handleSort}
      handleFilter={handleFilter}
      filter={filters[accessKey] || null}
    />
  );
};

const Table = forwardRef<ITableRef, React.PropsWithChildren<ITable>>((props, ref) => {
  const {
    isLoading = false,
    data = [],
    columns = [],
    isServerSide = false,
    totalRecords = 0,
    handleFilterPaginationOnChange = () => {},
    recordsPerPage = 10,
    tableName = '',
  } = props;
  const [allTableData, setAllTableData] = useState<Array<any>>([]);
  const [tableData, setTableData] = useState<Array<any>>([]);
  const [currentPageSize, setCurrentPageSize] = useState<number>(recordsPerPage);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalCounts, setTotalCounts] = useState<number>(0);
  const [sortedColumn, setSortedColumn] = useState<ISorting>({
    accessKey: '',
    field: '',
    order: null,
  });
  const [filters, setFilters] = useState({});
  const [advFilters, setAdvFilters] = useState<IAdvFilter[]>([]);
  const [sorting, setSorting] = useState<ISortingOptions | null>(null);
  const [clearFilterCount, setClearFilterCount] = useState<number>(0);
  const handleFilter = (filter: IFilter) => {
    if (filter.filterOneValue === '' && filter.filterTwoValue === '') {
      if (filters[filter.accessKey]) {
        delete filters[filter.accessKey];
        handlePersistFilter(filters, tableName, currentPage, currentPageSize, sortedColumn);
        const advFiltersArr = handleGenerateAdvFilter(filters);
        setAdvFilters(advFiltersArr);
        setCurrentPage(1);
        handleFilterPaginationOnChange(1, currentPageSize, advFiltersArr, sorting);
      }
    } else {
      const objFilter = {
        ...filters,
        [filter.accessKey]: filter,
      };
      setFilters(objFilter);
      handlePersistFilter(objFilter, tableName, currentPage, currentPageSize, sortedColumn);
      const advFiltersArr = handleGenerateAdvFilter(objFilter);
      setAdvFilters(advFiltersArr);
      setCurrentPage(1);
      handleFilterPaginationOnChange(1, currentPageSize, advFiltersArr, sorting);
    }
  };

  const handleSort = (
    field: string,
    accessKey: string,
    order: string | null,
    nestedObject: string | undefined,
    type?: string,
  ) => {
    const sortedObject: ISorting = {
      field: order === null ? '' : field,
      accessKey,
      order,
      nestedObject,
      type,
    };
    handlePersistSorting(filters, sortedObject, tableName, currentPage, currentPageSize);
    setSortedColumn(sortedObject);
    if (isServerSide) {
      const sort =
        order === null
          ? null
          : {
              sortProperty: field,
              sortBy: order,
            };
      setSorting(sort);
      handleFilterPaginationOnChange(currentPage, currentPageSize, advFilters, sort);
      return;
    }

    if (order) {
      setAllTableData(sortingData([...allTableData], order, field, type));
    } else {
      setAllTableData([...data]);
    }
  };

  const columnHelper = createColumnHelper<any>();

  const shellColumns = columns.map((column) => {
    return columnHelper.accessor(column.accessKey, {
      cell: (info) => {
        return column.render ? column.render(info.row.original) : info.renderValue();
      },
      header: () => ColumnTitleComp(column, sortedColumn, handleSort, handleFilter, filters),
    });
  });

  const handlePersistPagination = (page: number, pageSize: number) => {
    if (tableName) {
      let storedFilter = sessionStorage.getItem(TABLE_FILTER);
      storedFilter = storedFilter !== null ? JSON.parse(storedFilter) : {};
      if (storedFilter?.[tableName]) {
        storedFilter[tableName] = {
          filter: filters,
          currentPage: page,
          currentPageSize: pageSize,
          sorting: sortedColumn,
        };
      }
      sessionStorage.setItem(TABLE_FILTER, JSON.stringify(storedFilter));
    }
  };

  const handlePaginationChange = (page: number, pageSize: number) => {
    handlePersistPagination(page, pageSize);
    setCurrentPage(page);
    if (isServerSide) {
      handleFilterPaginationOnChange(page, pageSize, advFilters, sorting, true);
      return;
    }

    const objData = allTableData.slice((page - 1) * pageSize, page * pageSize);
    setTableData(objData);
  };

  const handlePageSizeChange = (value) => {
    setCurrentPage(1);
    setCurrentPageSize(value);
    handlePersistPagination(1, value);
    if (isServerSide) {
      handleFilterPaginationOnChange(1, value, advFilters, sorting, true);
    }
  };

  useEffect(() => {
    if (!isServerSide) {
      const numberOfPages = Math.ceil(allTableData.length / currentPageSize);
      handlePaginationChange(numberOfPages >= currentPage ? currentPage : 1, currentPageSize);
    }
  }, [allTableData, currentPageSize]);

  useEffect(() => {
    if (isServerSide) {
      setTableData(data);
      setTotalCounts(totalRecords);
    } else {
      setTotalCounts(data.length);
      if (sortedColumn.field !== '') {
        const sortedData = sortingData(
          data,
          sortedColumn.order,
          sortedColumn.field,
          sortedColumn.type,
        );
        setAllTableData(sortedData);
        return;
      }
      setAllTableData(data);
    }
  }, [currentPageSize, data]);

  useEffect(() => {
    if (tableName) {
      let storedFilter = sessionStorage.getItem(TABLE_FILTER);
      storedFilter = storedFilter !== null ? JSON.parse(storedFilter) : {};

      if (storedFilter?.[tableName]) {
        setFilters(storedFilter[tableName]?.filter);
        setCurrentPage(storedFilter[tableName]?.currentPage);
        setCurrentPageSize(storedFilter[tableName]?.currentPageSize);
        const requestPayload = handleGenerateAdvFilter(storedFilter[tableName]?.filter);
        setAdvFilters(requestPayload);
        const objSortedColumn: ISorting = storedFilter[tableName]?.sorting;
        if (objSortedColumn) {
          setSortedColumn(objSortedColumn);
          const sort =
            objSortedColumn.order === null
              ? null
              : {
                  sortProperty: objSortedColumn.field,
                  sortBy: objSortedColumn.order,
                };
          setSorting(sort);
        }
      }
    }
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      resetPagination() {
        setCurrentPage(1);
      },
      getPageSize() {
        return currentPageSize;
      },
      refreshData() {
        handleFilterPaginationOnChange(1, currentPageSize, advFilters, sorting);
      },
      getFilterData(resetCurrentPage = false) {
        if (resetCurrentPage) {
          setCurrentPage(1);
        }
        let storedFilter = sessionStorage.getItem(TABLE_FILTER);
        storedFilter = storedFilter !== null ? JSON.parse(storedFilter) : {};
        if (tableName && storedFilter?.[tableName]) {
          setCurrentPage(storedFilter[tableName].currentPage);
          const objSortedColumn: ISorting = storedFilter[tableName]?.sorting || {
            field: '',
            order: null,
          };
          return {
            currentPage: storedFilter[tableName].currentPage,
            currentPageSize: storedFilter[tableName].currentPageSize,
            advFilters: handleGenerateAdvFilter(storedFilter[tableName].filter),
            sorting:
              objSortedColumn.order === null
                ? null
                : {
                    sortProperty: objSortedColumn.field,
                    sortBy: objSortedColumn.order,
                  },
          };
        }
        return {
          currentPage: resetCurrentPage ? 1 : currentPage,
          currentPageSize,
          advFilters,
          sorting,
        };
      },
      clearFilters() {
        setFilters({});
        let storedFilter = sessionStorage.getItem(TABLE_FILTER);
        storedFilter = storedFilter !== null ? JSON.parse(storedFilter) : {};
        if (storedFilter?.[tableName]) {
          delete storedFilter[tableName];
        }
        sessionStorage.setItem(TABLE_FILTER, JSON.stringify(storedFilter));
        setAdvFilters([]);
        setClearFilterCount((prevState) => prevState + 1);
        setCurrentPage(1);
        handleFilterPaginationOnChange(1, currentPageSize, [], sorting);
      },
    }),
    [currentPage, currentPageSize, advFilters, sorting, clearFilterCount],
  );

  const table = useReactTable({
    data: tableData,
    columns: shellColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <TableContainer>
      {isLoading && <LoaderComp />}
      <div className='table-container'>
        <ShellTable className='shell-table' data-testid='table' size='small'>
          <THead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={uuidv4()}>
                {headerGroup.headers.map((header) => (
                  <Th key={uuidv4()} colSpan={header.colSpan}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </Th>
                ))}
              </Tr>
            ))}
          </THead>
          <TBody>
            {table.getRowModel().rows.map((row) => (
              <Tr key={uuidv4()} style={row.original.style}>
                {row.getVisibleCells().map((cell) => (
                  <Td key={uuidv4()}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            ))}
          </TBody>
          {!table.getRowModel().rows.length && (
            <TBody>
              <Tr>
                <Td colSpan={shellColumns.length} className='no-data-cell'>
                  <div className='title_wrapper'>
                    {totalCounts === 0 && !isLoading ? <h3>No data found</h3> : null}
                  </div>
                </Td>
              </Tr>
            </TBody>
          )}
        </ShellTable>
      </div>
      {data.length > 0 && (
        <div className='pagination-container'>
          <div className='total-rows'>{`Total ${totalCounts} items`}</div>
          <Pagination
            onChange={handlePaginationChange}
            current={currentPage}
            pageSize={currentPageSize}
            total={totalCounts}
            position='right'
            data-testid='pagination'
            size='small'
          />
          <Select
            placeholder='Page Size'
            showSearch={false}
            onChange={handlePageSizeChange}
            value={currentPageSize}
            className='select-page-size'
            data-testid='page-size'
            allowClear={false}
            size='small'
          >
            {PAGINATION_OPTIONS.map((item) => {
              return (
                <Option data-testid={`option_${item}`} key={uuidv4()} value={item}>
                  {item}
                </Option>
              );
            })}
          </Select>
        </div>
      )}
    </TableContainer>
  );
});

export default Table;
